import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { useForm } from 'react-hook-form';
import { UtilisateurSearchList, useModal } from '@europrocurement/flexy-components';
import { UtilisateursJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { FormalisteJsonldFormalisteRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';

export type FormalisteAssignModalProps = {
    /** Liste des formalistes disponibles pour l'assignation */
    formalistes: FormalisteJsonldFormalisteRead[];
    /** Fonction de recherche de formalistes par nom */
    onSearchFormalistes?: (searchTerm: string) => Promise<FormalisteJsonldFormalisteRead[]>;
    /** Formaliste actuellement sélectionné */
    selectedFormaliste?: UtilisateursJsonldUserworkgroupRead | null;
    /** Fonction appelée lors de l'assignation d'un formaliste */
    onFormalisteAssign: (formaliste: UtilisateursJsonldUserworkgroupRead, message?: string) => void;
    /** État de chargement pendant l'assignation */
    isLoading?: boolean;
    /** Titre personnalisé pour la modal */
    title?: string;
    /** Texte du bouton de soumission */
    submitButtonText?: string;
};

/**
 * Modal d'assignation d'un formaliste à un dossier.
 * Conçue pour être utilisée avec useModal() et modalActions.call()
 */
const FormalisteAssignModal: React.FunctionComponent<FormalisteAssignModalProps> = function ({
    formalistes,
    onSearchFormalistes,
    selectedFormaliste: initialSelectedFormaliste,
    onFormalisteAssign,
    isLoading = false,
    title = 'Assigner un formaliste',
    submitButtonText = 'Assigner',
}) {
    const { palette } = useTheme();
    const { modalActions } = useModal();

    // État local pour le formaliste sélectionné
    const [selectedFormaliste, setSelectedFormaliste] =
        useState<UtilisateursJsonldUserworkgroupRead | null>(initialSelectedFormaliste || null);

    // État pour les résultats de recherche
    const [displayedFormalistes, setDisplayedFormalistes] =
        useState<FormalisteJsonldFormalisteRead[]>(formalistes);
    const [isSearching, setIsSearching] = useState(false);

    // Synchronisation avec la prop externe
    useEffect(() => {
        setSelectedFormaliste(initialSelectedFormaliste || null);
    }, [initialSelectedFormaliste]);

    // Mettre à jour les formalistes affichés quand la liste initiale change
    useEffect(() => {
        setDisplayedFormalistes(formalistes);
    }, [formalistes]);

    // Fonction de recherche avec debounce
    const handleSearch = useCallback(
        async (searchTerm: string) => {
            setIsSearching(true);
            try {
                const results = await onSearchFormalistes?.(searchTerm);
                if (results) {
                    setDisplayedFormalistes(results);
                }
            } catch (error) {
                console.error('Erreur lors de la recherche de formalistes:', error);
            } finally {
                setIsSearching(false);
            }
        },
        [onSearchFormalistes],
    );

    /**
     * Configuration du formulaire pour le message optionnel
     */
    const formStructure: FormStructure[] = [
        {
            type: 'textarea',
            name: 'message',
            inputlabel: 'Message (optionnel)',
            inputProps: {
                multiline: true,
                maxRows: Infinity,
                placeholder: 'Ajouter une note concernant cette assignation...',
            },
            sx: {
                minHeight: '100px',
                padding: '8px 20px',
                border: `1px solid ${palette.grey['300']}`,
                borderRadius: '12px',
                ':hover': {
                    border: `1px solid ${palette.grey['900']}`,
                },
                ':focus-within': {
                    border: `1px solid ${palette.primary.main}`,
                },
                transition: 'all ease-in-out 300ms',
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
            },
        },
    ];

    const formContext = useForm({
        mode: 'onTouched',
    });

    const formProps = {
        formContext,
        formObject: {},
        onSubmit: () => {},
        submitButton: {
            displayed: false,
        },
    };

    /**
     * Gère l'assignation du formaliste avec message optionnel
     */
    const handleAssignFormaliste = () => {
        if (!selectedFormaliste || isLoading) {
            return;
        }

        const { message } = formContext.getValues();
        const trimmedMessage = message?.trim();

        onFormalisteAssign(
            selectedFormaliste,
            trimmedMessage && trimmedMessage.length > 0 ? trimmedMessage : undefined,
        );

        // Fermer la modal après assignation
        modalActions.close();
    };

    /**
     * Détermine si le bouton d'assignation doit être actif
     */
    const isAssignButtonEnabled = selectedFormaliste && !isLoading;

    return (
        <Box
            sx={{
                width: 600,
                padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
            }}
        >
            {/* Titre */}
            <Typography variant="l2dh6">{title}</Typography>

            {/* Corps de la modal */}
            <Box sx={{ width: '100%', gap: '8px', display: 'grid' }}>
                {/* Liste des formalistes */}
                <UtilisateurSearchList
                    users={displayedFormalistes}
                    selectedUser={selectedFormaliste}
                    setSelectedUser={setSelectedFormaliste}
                    listTitle="Formalistes disponibles"
                    onSearch={handleSearch}
                    isSearching={isSearching}
                />

                {/* Zone de message */}
                <Box>
                    <FlexyForm
                        formStructure={formStructure}
                        {...formProps}
                    />
                </Box>
            </Box>

            {/* Actions */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'end',
                }}
            >
                <Box sx={{ display: 'flex', gap: '16px' }}>
                    {/* Bouton Annuler */}
                    <Button
                        onClick={() => modalActions.close()}
                        variant="outlined"
                        disabled={isLoading}
                        sx={{
                            padding: '16px 24px',
                            fontSize: '16px',
                        }}
                    >
                        Annuler
                    </Button>

                    {/* Bouton d'assignation avec état de chargement */}
                    {isLoading ? (
                        <Button
                            variant="contained"
                            disabled
                            sx={{
                                padding: '16px 24px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minWidth: '6rem',
                                fontSize: '16px',
                            }}
                        >
                            <CircularProgress
                                color="info"
                                sx={{
                                    maxHeight: '15px',
                                    maxWidth: '15px',
                                    color: 'white',
                                }}
                            />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleAssignFormaliste}
                            variant="contained"
                            disabled={!isAssignButtonEnabled}
                            sx={{
                                padding: '16px 24px',
                                fontSize: '16px',
                                transition: 'all ease-in-out 300ms',
                                backgroundColor: isAssignButtonEnabled
                                    ? palette.primary.main
                                    : palette.grey[300],
                                ':hover': {
                                    backgroundColor: isAssignButtonEnabled
                                        ? palette.primary.dark
                                        : palette.grey[300],
                                },
                            }}
                        >
                            {submitButtonText}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default FormalisteAssignModal;
